import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Select, Typography, Space, ConfigProvider, theme as antdTheme } from 'antd';
import type { GetRef, SelectProps } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ReactComponent as KeyboardIcon } from '../assets/header/keyboard.svg';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const { Text } = Typography;

type SelectRef = GetRef<typeof Select>;
type ValueType = string | number;

type OptionType = {
  value: ValueType;
  label: React.ReactNode;
};

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  placeholder?: string;
  shortcutLabel?: string;
  fetchOptions: (q: string) => Promise<OptionType[]>;
  to: (id: ValueType) => string;
};

export default function QuickSearchModal({
  open,
  onClose,
  title,
  placeholder = 'Search...',
  shortcutLabel = 'Alt/Option + Key',
  fetchOptions,
  to,
}: Props) {
  const navigate = useNavigate();
  const selectRef = useRef<SelectRef>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [options, setOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(false);

  const { theme } = useSelector((state: any) => state.global);
  const isDarkMode = theme === 'dark';

  const runSearch = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const list = await fetchOptions(trimmed);
        setOptions(list);
      } finally {
        setLoading(false);
      }
    },
    [fetchOptions],
  );

  const onSearch = (value: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => runSearch(value), 400);
  };

  useEffect(() => {
    if (!open) {
      setOptions([]);
      setLoading(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [open]);

  const handleSelect: SelectProps<ValueType, OptionType>['onSelect'] = (val) => {
    navigate(to(val));
    onClose();
  };

  // TESKARI THEME TOKENS: dark = oq, light = qora
  const themeTokens = isDarkMode
    ? {
        colorPrimary: '#1890ff',
        colorBgContainer: '#fff', // oq modal fon
        colorBgElevated: '#fafafa', // select fon
        colorText: '#000', // qora text
        colorTextSecondary: '#595959',
        colorBorder: '#d9d9d9',
        colorBorderSecondary: '#f0f0f0',
      }
    : {
        colorPrimary: '#1890ff',
        colorBgContainer: '#1f1f1f', // qora modal fon
        colorBgElevated: '#141414', // select fon
        colorText: '#f0f0f0', // oq text
        colorTextSecondary: '#bfbfbf',
        colorBorder: '#303030',
        colorBorderSecondary: '#3a3a3a',
      };

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: themeTokens,
      }}
    >
      <Modal
        open={open}
        onCancel={onClose}
        closable={false}
        footer={null}
        width={640}
        bodyStyle={{
          paddingTop: 12,
          background: themeTokens.colorBgContainer,
          color: themeTokens.colorText,
        }}
        afterOpenChange={(isOpen) => {
          if (isOpen) setTimeout(() => selectRef.current?.focus(), 0);
        }}
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <Space size={10}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(24,144,255,0.1)',
                }}
              >
                <SearchOutlined style={{ fontSize: 16, color: '#1890ff' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <Text strong style={{ fontSize: 16, color: themeTokens.colorText }}>
                  {title}
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, color: themeTokens.colorTextSecondary }}
                >
                  Search and jump directly to the selected page
                </Text>
              </div>
            </Space>
            <div
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                border: `1px solid ${themeTokens.colorBorder}`,
                background: themeTokens.colorBgElevated,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap',
              }}
            >
              <KeyboardIcon style={{ fontSize: 10, color: '#9B9DAA' }} />
              <Text
                type="secondary"
                style={{ fontSize: 12, color: themeTokens.colorTextSecondary }}
              >
                {shortcutLabel}
              </Text>
            </div>
          </div>
        }
      >
        <div
          style={{
            padding: 12,
            borderRadius: 14,
            background: themeTokens.colorBgElevated,
            border: `1px solid ${themeTokens.colorBorderSecondary}`,
          }}
        >
          <Select<ValueType, OptionType>
            ref={selectRef}
            showSearch
            placeholder={placeholder}
            filterOption={false}
            onSearch={onSearch}
            onSelect={handleSelect}
            options={options}
            loading={loading}
            allowClear
            size="large"
            style={{
              width: '100%',
              background: themeTokens.colorBgElevated,
              color: themeTokens.colorText,
            }}
            notFoundContent={
              loading ? (
                <Text type="secondary" style={{ color: themeTokens.colorTextSecondary }}>
                  Searching...
                </Text>
              ) : (
                <Text type="secondary" style={{ color: themeTokens.colorTextSecondary }}>
                  Start typing to search
                </Text>
              )
            }
          />
        </div>

        <div
          style={{
            marginTop: 14,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Text type="secondary" style={{ fontSize: 12, color: themeTokens.colorTextSecondary }}>
            Press <b>Enter</b> to select, <b>Esc</b> to close
          </Text>
          <Text type="secondary" style={{ fontSize: 12, color: themeTokens.colorTextSecondary }}>
            Results open the related details page
          </Text>
        </div>
      </Modal>
    </ConfigProvider>
  );
}
